import React from "react";
import { Calendar } from "primereact/calendar";
import { Tooltip } from "primereact/tooltip";
import { formatDateToDDMMYYYY, parseDDMMYYYY } from "../../utils/dateUtils";

interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  onBlur?: () => void;
  label: string;
  required?: boolean;
  errors?: string[];
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  tooltipContent?: string;
  tooltipId?: string;
  placeholder?: string;
  id?: string;
  name?: string;
  tabIndex?: number;
}

/**
 * Date picker component for DD/MM/YYYY format
 * Uses PrimeReact Calendar component
 */
export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  onBlur,
  label,
  required = false,
  errors,
  disabled = false,
  id,
  name,
  minDate,
  maxDate,
  tooltipContent,
  tooltipId,
  placeholder = "DD/MM/YYYY",
  tabIndex,
}) => {
  // Convert Date to string for display (DD/MM/YYYY)
  const displayValue = value ? formatDateToDDMMYYYY(value) : null;

  const handleDateChange = (e: any) => {
    if (!e.value) {
      onChange(null);
      return;
    }

    // Calendar returns a Date object, convert to our format
    const date = e.value as Date;
    onChange(date);
  };

  const tooltipTargetId =
    tooltipId || `date-${label.toLowerCase().replace(/\s+/g, "-")}`;

  const fieldId = id || name || tooltipTargetId;

  return (
    <div className="field">
      <label htmlFor={fieldId} className="input-label mb-2">
        {label} {required && "*"}
        {tooltipContent && (
          <>
            {" "}
            <span id={tooltipTargetId} className="pi pi-info-circle" />
            <Tooltip
              target={`#${tooltipTargetId}`}
              content={tooltipContent}
              position="right"
              className="custom-tooltip tooltip-custom-format"
            />
          </>
        )}
      </label>
      <Calendar
        id={id}
        name={name}
        value={value}
        onChange={handleDateChange}
        onBlur={onBlur}
        dateFormat="dd/mm/yy"
        icon="pi pi-calendar"
        className={`w-full calendar-field ${errors && errors.length > 0 ? "p-invalid" : ""}`}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        placeholder={placeholder}
        tabIndex={tabIndex}
        aria-describedby={
          errors && errors.length > 0 ? `${id || name}-error` : tooltipId
        }
        aria-invalid={!!(errors && errors.length > 0)}
        aria-required={required}
      />
      {errors && errors.length > 0 && (
        <small id={`${id || name}-error`} className="p-error" role="alert">
          {errors[0]}
        </small>
      )}
    </div>
  );
};
